import React, { useState } from 'react';
import { Link as RouterLink } from 'gatsby';
import {
    Box,
    Flex,
    Link,
    Image,
    Text,
    IconButton,
    useDisclosure,
    Stack
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';

interface NavigationItem {
    link: string;
    label: string;
    dataFrom: string;
    subLinks?: any;
}

interface NavigationBarProps {
    items: NavigationItem[];
    facilityName: string;
    logo?: string; // Add logo prop
}

const NavigationBar: React.FC<NavigationBarProps> = ({ items, facilityName, logo }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [isDropdownOpen, setDropdownOpen] = useState(false);

    const toggleDropdown = () => {
        setDropdownOpen(!isDropdownOpen);
    };

    const getActiveClass = (link: string) => {
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        if (link === "/") {
            return currentPath === "/" ? "active" : "";
        }
        if (link.includes('pay-rent')) {
            return currentPath.includes('pay-rent') ? "active" : "";
        }

        return currentPath.includes(link) ? "active" : "";
    };

    return (
        <Flex
            bg="common.white"
            color='common.grey'
            height='77px'
            justifyContent="center"
            alignItems='center'
            boxShadow={'0 5px 5px 0 rgba(0, 0, 0, 0.09)'}
            flexWrap='wrap'
        >
            <Flex
                justify="space-around"
                align="center"
                display={{ base: 'none', md: 'flex' }}
                height='100%'
                width='100%'
            >
                <Box>
                    <Link as={RouterLink} to="/" fontSize="xl" fontWeight="bold">
                        {logo ? (
                            <div className="logo-container">
                                <Image src={logo} alt={facilityName} maxH="65px" />
                            </div>
                        ) : (
                            <Text textStyle={"h4"} >
                                {facilityName}
                            </Text>
                        )}
                    </Link>
                </Box>
                <Flex align="center" h='100%'>
                    {items.map((item, index) => {
                        if (item.subLinks) {
                            return (
                                <div
                                    className={`nav-link-container dropdown ${getActiveClass(item.link)}`}
                                    onClick={toggleDropdown}
                                >
                                    <Text textStyle={'h4'} >
                                        {item.label}
                                    </Text>
                                    {true && (
                                        <Box className="dropdown-content">
                                            {item.subLinks.map((subItem: any, subIndex: any) => (
                                                <Text textStyle={'subtitle'} >
                                                    <Link
                                                        as={RouterLink}
                                                        to={subItem.link}
                                                        key={subIndex}
                                                        mx={2}
                                                        fontWeight="large"
                                                        state={{
                                                            dataFrom: item.dataFrom
                                                        }}
                                                        _active={{
                                                            color: "brand.tertiary",
                                                        }}
                                                        className={`nav-link ${getActiveClass(item.link)}`}
                                                        textDecoration={'none'}
                                                        _hover={{
                                                            textDecoration: 'none',
                                                            color: "brand.tertiary",
                                                            transition: 'color .3s ease'
                                                        }}
                                                        margin={0}
                                                    >
                                                        {subItem.label}
                                                    </Link>
                                                </Text>
                                            ))}
                                        </Box>
                                    )}
                                </div>
                            )
                        } else {
                            return (
                                <div className={`nav-link-container ${getActiveClass(item.link)}`} style={{ height: '100%' }}>
                                    <Text textStyle={'subtitle'} h='100%' display='flex' alignItems='center'>
                                        <Link
                                            as={RouterLink}
                                            to={item.link}
                                            key={index}
                                            mx={2}
                                            fontWeight="large"
                                            state={{
                                                dataFrom: item.dataFrom
                                            }}
                                            _active={{
                                                color: "brand.tertiary",
                                            }}
                                            className={`nav-link ${getActiveClass(item.link)}`}
                                            textDecoration={'none'}
                                            _hover={{
                                                textDecoration: 'none',
                                                color: "brand.tertiary",
                                                transition: 'color .3s ease'
                                            }}
                                            margin={0}
                                        >
                                            {item.label.toUpperCase()}
                                        </Link>
                                    </Text>
                                </div>
                            )
                        }
                    })}
                </Flex>
            </Flex>
            {
                <Flex
                    display={{ base: 'flex', md: 'none' }}
                    flexDirection={'row'}
                    justifyContent={"space-between"}
                    align={'center'}
                    px={'16px'}
                    height='77px'
                    width='100%'
                >
                    {/* <Text textStyle={"h4"} >
                        {facilityName}
                    </Text> */}
                    <Link as={RouterLink} to="/" fontSize="xl" fontWeight="bold">
                        {logo ? (
                            <div className="logo-container">
                                <Image src={logo} alt={facilityName} maxH="65px" />
                            </div>
                        ) : (
                            <Text textStyle={"h4"} >
                                {facilityName}
                            </Text>
                        )}
                    </Link>
                    <IconButton
                        size={'lg'}
                        icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
                        aria-label={'Open Menu'}
                        display={{ md: 'none' }}
                        color='black'
                        border='2px solid var(--chakra-colors-border-green)'
                        backgroundColor='white'
                        onClick={isOpen ? onClose : onOpen}
                    />
                </Flex>
            }
            {isOpen ? (
                <Box pb={4} display={{ md: 'none' }} background='white' width='100vw'>
                    <Stack as={'nav'} >
                        {items.map((item, index) => (
                            <Text
                                textStyle={'subtitle'}
                                textAlign={'center'}
                                display='flex'
                                justifyContent='center'
                            >
                                <Link
                                    as={RouterLink}
                                    to={item.link}
                                    key={index}
                                    mx={2}
                                    fontWeight="large"
                                    className={`nav-link small ${getActiveClass(item.link)}`}
                                    state={{
                                        dataFrom: item.dataFrom
                                    }}
                                    _active={{
                                        color: "brand.tertiary",
                                    }}
                                    textDecoration={'none'}
                                    _hover={{
                                        textDecoration: 'none',
                                        color: "brand.tertiary",
                                        transition: 'color .3s ease'
                                    }}
                                    margin={0}
                                >
                                    {item.label.toUpperCase()}
                                </Link>
                            </Text>
                        ))}
                    </Stack>
                </Box>
            ) : null}
        </Flex>
    );
};

export default NavigationBar;
